<template>
  <main id="card-theme" class="flex flex-col gap-6 w-full">
    <div class="bg-white">
      <Loader v-if="loading" :loader="loading" loaderSize="h-96" />

      <div v-else id="card-theme-content" class="flex flex-col gap-6">
        <section id="logo-preview" class="flex flex-col gap-3">
          <h3 class="text-lg font-medium">{{ $t("theme.org.logo") }}</h3>
          <div
            class="flex justify-center items-center h-32 bg-gray-200 rounded-md"
          >
            <Logo v-if="logo.length === 0" />
            <img
              v-else
              :src="getFileUrl(logo[0]?.image!)"
              alt="logo"
              class="h-20 object-contain"
              loading="lazy"
            />
          </div>
        </section>

        <div id="partners-preview" class="mb-5">
          <p class="font-bold text-lg mb-2">{{ $t("theme.org.partners") }}</p>

          <div
            v-if="partners.length === 0"
            class="flex flex-wrap justify-center items-center min-h-[150px] bg-gray-200 rounded-md gap-5"
          >
            <img
              v-for="(img, index) in images"
              :key="index"
              :src="img"
              :alt="img"
              class="w-fit h-8 invert"
              loading="lazy"
            />
          </div>

          <div
            v-else
            class="flex flex-wrap justify-center items-center min-h-[150px] bg-gray-200 rounded-md gap-5"
          >
            <img
              v-for="(src, index) in partners"
              id="partners-imgs"
              :key="index"
              :src="getFileUrl(src.image!)"
              class="w-fit mx-2 h-10"
              loading="lazy"
            />
          </div>
        </div>

        <section class="flex flex-col gap-3">
          <h3 class="text-lg font-medium">{{ $t("theme.org.primary") }}</h3>
          <PreviewColors :color="colors?.primary" />
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="text-lg font-medium">{{ $t("theme.org.secondary") }}</h3>
          <PreviewColors :color="colors?.secondary" />
        </section>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { useThemeStore } from "@/store/theme";
import { getFileUrl } from "@/utils/generics.util";
import PreviewColors from "@/views/EditUser/Theme/preview-colors.vue";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

export default defineComponent({
  name: "ThemeInfo",
  components: { PreviewColors },
  props: {
    orgId: String,
    domain: String,
  },
  setup(props) {
    const storeTheme = useThemeStore;
    const router = useRouter();
    const loading = ref(true);
    const colors = computed(() => {
      return {
        primary: storeTheme.filesById.theme?.colors["h-primary"],
        secondary: storeTheme.filesById.theme?.colors["h-secondary"],
      };
    });

    const requireImages: any = require.context(
      "../../assets/images/partners",
      false,
      /\.(png|jpe?g|svg)$/
    );

    const logo = computed(() =>
      storeTheme.filesById.images.filter((e) => e.type === "LOGO")
    );

    const partners = computed(() =>
      storeTheme.filesById.images.filter((e) => e.type === "PARTNERS")
    );

    const images = requireImages.keys().map(requireImages);

    onMounted(async () => {
      await storeTheme
        .getFilesById(props.orgId!)
        .finally(() => (loading.value = false));
    });

    return {
      storeTheme,
      router,
      colors,
      images,
      logo,
      partners,
      loading,
      getFileUrl,
    };
  },
});
</script>

<style lang="scss">
#card-theme {
  max-width: 100%;
  margin: 0 auto;
}

img {
  max-width: 100%;
  height: auto;
}
</style>
